/**
 * PORT (touch screens with the wide view): the battle's weapon sheet (menu_weapon_sheet) for fingers, laid out by the
 * wide screen (ui/wide-screen.ts, ScreenDress.custom) whenever the shown area changes.
 *
 * The original's sheet fills its 807x600 window: 80 cells of 50 px in 8 columns, a description panel beside them, text
 * of 12 px, a close button of 44x32 px and page arrows of 28 px. On a phone in landscape (0.53-0.69 CSS px per px) that
 * is 28-36 CSS px cells, 6-8 CSS px text and a 19-30 CSS px close button, while the screen shows a much wider area. With
 * the touch controls and the wide view (a phone, a tablet):
 *  - the sheet's frame spans the whole shown safe area (its left and right parts moved out to its edges, the light panel
 *    of the cells, the dark description panel and the frame's body stretched between: pieces of the frame's art; its
 *    height the shown area's, a tablet's too);
 *  - the cells are CELL_CSS CSS px apart (never less than the original's 50 px: a tablet) in as many columns and rows
 *    as fit (FWP.sheet_layout: new_interf.render pages the weapons by that many; when there are more pages the grid's
 *    last two cells hold the page arrows, at least ARROW_CSS CSS px);
 *  - the description (the weapon's name and text) at TEXT_CSS CSS px (never less than the original's): beside the cells
 *    in the dark panel where it fits there (a 19.5:9 - 21:9 phone, a tablet; while no weapon is held it describes the
 *    worm's weapon: new_interf.sheet_idle_text), else over the cells while a finger holds a weapon (a 16:9 phone), on
 *    the side away from that weapon, the longest texts a little smaller there (MIN_TEXT), mouse-transparent there (the
 *    finger lifted over it is still the pressed cell's tap: the pick);
 *  - the note at the top (the timer stops / runs) at TEXT_CSS CSS px, and a footer row under the cells for the touch
 *    controls' menu info (the long-press hint, an online battle's clock);
 *  - the sheet's own close button lies under the touch controls' close button (FWP.touch_close_rect, drawn nearly
 *    transparent): one close button is seen, and the back action still closes the sheet by it (mobile-port.ts).
 * The scanner has a layout of its own (ui/battle-scanner.ts); its close arrow lies under the touch controls' close
 * button the same way. The battle's HUD is hidden while the sheet is open (as the original's sheet covered the window;
 * wide-screen.ts `under`): no second clock beside it, nothing of it above a tablet's sheet.
 * Drawing and placement only: the cells and buttons are the game's (their clicks and hovers as before), only the
 * number of weapons per page follows the grid.
 */
import { Rectangle, Sprite } from '@flash';

/** A rectangle in stage coordinates. */
interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** What a screen's own layout reads (ui/wide-screen.ts). */
export interface ScreenContext {
  /** The stage: visibleRect, safeRect, cssScale ($visibleArea: a wide host). */
  stage: any;
  /** The game: touch_zones, touch_close_rect, sheet_grid, menu_info_rect, _interf. */
  fwp: any;
}

/** A screen's own layout (ScreenDress.custom): again whenever the wide screen lays the screen out, and every frame. */
export interface ScreenLayout {
  layout(root: any, ctx: ScreenContext): void;
  frame?(root: any, ctx: ScreenContext): void;
}

/** The pitch of the cells aimed at (CSS px): the cells' art ~45 CSS px, their hit areas 48. */
export const CELL_CSS = 48;
/** The text size of the description and the note (CSS px). */
export const TEXT_CSS = 12;
/** The page arrows' size (CSS px) at least. */
export const ARROW_CSS = 44;

/** The original's cell pitch and layout (stage px): new_interf.render. */
const PITCH = 50;
const COLS = 8;
/** The frame's parts (the character 145, in the sheet's coordinates): its left part up to the light panel, a column of
 * the light panel, the dark panel, a column of the frame's body right of it, its right part (the body, the stripe). */
const FRAME_ID = 145;
const F = { x0: 9, left: 74, light: 200, dark0: 481, dark1: 729, body: 734, right: 729, x1: 800, y0: 10, top: 41, bottom: 577, y1: 589 };
/** The description panel's width at scale 1 (px of the fields as the original draws them; the original's is 248). */
const DESC_W = 300;
/** The description panel's width over the cells at scale 1 (a 16:9 phone: the longest text in the fewest lines). */
const DESC_OVER_W = 440;
/** The longest description's height (px at DESC_W, as the game has them, the name above it included). */
const DESC_H = 336;

/** A piece of a copy of the display object `o`: its part `src` (its parent's coordinates) stretched over `dst`. */
export interface Piece {
  sprite: any;
  place(src: Area, dst: Area | null): void;
}

export function pieceOf(o: any, name: string): Piece | null {
  const sym = o.$sym;
  if (!sym || typeof sym.instantiate !== 'function' || !o.$charId) return null;
  const copy = sym.instantiate(o.$charId);
  if (!copy) return null;
  if (typeof copy.gotoAndStop === 'function') copy.gotoAndStop(1);
  const s = new Sprite();
  s.name = name.startsWith('$') ? name : '$wide_' + name;
  s.mouseEnabled = false;
  s.mouseChildren = false;
  copy.transform.matrix = o.transform.matrix;
  s.addChild(copy);
  return {
    sprite: s,
    place(src: Area, dst: Area | null) {
      if (!dst || !(dst.width > 0.01 && dst.height > 0.01)) {
        s.visible = false;
        s.x = -10000;
        return;
      }
      s.visible = true;
      s.scrollRect = new Rectangle(src.x, src.y, src.width, src.height);
      s.scaleX = dst.width / src.width;
      s.scaleY = dst.height / src.height;
      s.x = dst.x;
      s.y = dst.y;
    },
  };
}

/** A child's place as the original has it. */
export interface Orig {
  x: number;
  y: number;
  sx: number;
  sy: number;
  width: number | null;
  height: number | null;
  alpha: number;
  visible: boolean;
  index: number;
  /** Its clip to the window (ui/wide-screen.ts dresses the screen's children with it): off where the layout moves it. */
  clip: boolean;
}

export function remember(map: Map<any, Orig>, o: any, root: any): void {
  if (!o || map.has(o)) return;
  const text = typeof o.textWidth === 'number';
  map.set(o, { x: o.x, y: o.y, sx: o.scaleX, sy: o.scaleY, width: text ? o.width : null, height: text ? o.height : null, alpha: o.alpha, visible: o.visible, index: root.getChildIndex(o), clip: !!o.$designClip });
  o.$designClip = false;
}

export function restore(map: Map<any, Orig>, root: any): void {
  const byIndex = [...map.entries()].sort((a, b) => a[1].index - b[1].index);
  for (const [o, v] of byIndex) {
    o.scaleX = v.sx;
    o.scaleY = v.sy;
    o.x = v.x;
    o.y = v.y;
    if (v.width !== null) o.width = v.width;
    if (v.height !== null) o.height = v.height;
    o.alpha = v.alpha;
    o.$designClip = v.clip;
    if (o.parent === root && v.index < root.numChildren) root.setChildIndex(o, v.index);
  }
  map.clear();
}

/** The stage rectangle of the touch controls' close button (FWP.touch_close_rect), or null. */
export function closeRect(fwp: any): Area | null {
  const f = fwp && fwp.touch_close_rect;
  if (typeof f !== 'function') return null;
  try {
    const r = f();
    return r && r.width > 0 && r.height > 0 ? r : null;
  } catch {
    return null;
  }
}

/**
 * A close button of the game under the touch controls' close button (nearly transparent: still shown to the back
 * action, which taps it), or where the original has it without touch controls.
 */
export function placeCloseUnder(btn: any, at: Area | null, orig: Map<any, Orig>, root: any): void {
  if (!btn) return;
  if (!at) {
    const o = orig.get(btn);
    if (o) {
      btn.scaleX = o.sx;
      btn.scaleY = o.sy;
      btn.x = o.x;
      btn.y = o.y;
      btn.alpha = o.alpha;
      btn.$designClip = o.clip;
      if (btn.parent === root && o.index < root.numChildren) root.setChildIndex(btn, o.index);
      orig.delete(btn);
    }
    return;
  }
  remember(orig, btn, root);
  const o = orig.get(btn)!;
  // (over the screen's other children: the back action taps it where nothing else takes the tap)
  if (btn.parent === root) root.setChildIndex(btn, root.numChildren - 1);
  btn.scaleX = o.sx;
  btn.scaleY = o.sy;
  const b = btn.getBounds(root);
  const k = Math.min(1, (at.width * 0.8) / b.width, (at.height * 0.8) / b.height);
  btn.scaleX = o.sx * k;
  btn.scaleY = o.sy * k;
  const c = btn.getBounds(root);
  btn.x += at.x + at.width / 2 - (c.x + c.width / 2);
  btn.y += at.y + at.height / 2 - (c.y + c.height / 2);
  btn.alpha = 0.01;
}

function childOf(root: any, test: (c: any) => boolean): any {
  for (let i = 0; i < root.numChildren; i++) {
    const c = root.getChildAt(i);
    if (test(c)) return c;
  }
  return null;
}

/** The weapon sheet's touch layout state (on its root). */
interface SheetState {
  orig: Map<any, Orig>;
  pieces: Record<'l' | 'g' | 'd' | 'b' | 'r' | 'over', Piece | null> | null;
  frame: any;
  on: boolean;
  mode: 'side' | 'over';
  grid: { x: number; y: number; p: number; cols: number; rows: number } | null;
  /** The dark panel (side) or the room over the cells (over), and the text's scale. */
  panel: Area | null;
  u: number;
  /** The text's scale: TEXT_CSS CSS px, never smaller than the original draws it (a tablet). */
  t: number;
  cells: string;
  hover: string;
  /** The footer row under the cells: the touch controls' menu info (FWP.menu_info_rect). */
  info: Area | null;
  /** The touch controls' close button where the layout last placed the sheet's own under it. */
  closeKey: string;
  /** The description's text last fitted to its panel (placeDesc), and whether it lies over the cells now. */
  descKey: string;
  overShown: boolean;
  /** The description fields' own mouseEnabled while they let the finger through (letFingerThrough). */
  mouse: Map<any, boolean>;
}

function sheetState(root: any): SheetState {
  if (!root.$touchSheet) root.$touchSheet = { orig: new Map(), pieces: null, frame: null, on: false, mode: 'side', grid: null, panel: null, u: 1, t: 1, cells: '', hover: '', info: null, closeKey: '', descKey: '', overShown: false, mouse: new Map() } as SheetState;
  return root.$touchSheet as SheetState;
}

/**
 * The touch layout of the weapon sheet `root` now (FWP.sheet_layout): its grid (new_interf.render pages the weapons by
 * it) and the footer row for the touch controls' menu info; null: the original's layout.
 */
export function touchSheetLayout(root: any): { grid: { cols: number; rows: number }; info: Area; covered: boolean } | null {
  const st = root && (root.$touchSheet as SheetState | undefined);
  return st && st.on && st.grid && st.info ? { grid: { cols: st.grid.cols, rows: st.grid.rows }, info: st.info, covered: st.overShown } : null;
}

/** Whether a stage gets the touch layout of the weapon sheet (touch controls with the wide view). */
export function touchSheetOn(stage: any, fwp: any): boolean {
  return !!stage && !!stage.$visibleArea && !!fwp && typeof fwp.touch_zones === 'function';
}

export const WEAPON_SHEET: ScreenLayout = {
  layout(root, ctx) {
    const st = sheetState(root);
    const { stage, fwp } = ctx;
    {
      const cr0 = closeRect(fwp);
      st.closeKey = cr0 ? `${Math.round(cr0.x)},${Math.round(cr0.y)},${Math.round(cr0.width)}` : '';
    }
    const cont = root.in_game_weapon_container;
    const name = root.weapon_name;
    const desc = root.weapon_desc;
    const note = childOf(root, (c) => typeof c.textWidth === 'number' && /^instance\d+$/.test(String(c.name)));
    const frame = (st.frame ??= childOf(root, (c) => c.$charId === FRAME_ID));
    const prev = root.in_game_prev_page;
    const next = root.in_game_next_page;
    const close = root.close_wpsheet;
    const on = touchSheetOn(stage, fwp) && !!cont && !!name && !!desc && !!frame;
    const wasGrid = st.grid ? `${st.grid.cols}x${st.grid.rows}` : '';
    if (!on) {
      if (st.on) {
        restore(st.orig, root);
        for (const p of Object.values(st.pieces ?? {})) p?.place(F as any, null);
        frame.visible = true;
        name.visible = desc.visible = true;
        letFingerThrough(st, [name, desc], false);
      }
      st.on = false;
      st.grid = null;
      st.info = null;
      // (a tablet: its close button under the touch controls' one all the same)
      placeCloseUnder(close, fwp && typeof fwp.touch_zones === 'function' ? closeRect(fwp) : null, st.orig, root);
      if (wasGrid) regrid(fwp, root);
      return;
    }
    for (const o of [cont, name, desc, note, prev, next]) remember(st.orig, o, root);
    if (!st.pieces) {
      const at = root.getChildIndex(frame) + 1;
      const make = (n: string) => pieceOf(frame, 'sheet_' + n);
      st.pieces = { l: make('l'), g: make('g'), d: make('d'), b: make('b'), r: make('r'), over: make('over') };
      let i = at;
      for (const k of ['l', 'g', 'd', 'b', 'r'] as const) if (st.pieces[k]) root.addChildAt(st.pieces[k]!.sprite, i++);
      // The description over the cells lies over them (a 16:9 phone), its text over it.
      if (st.pieces.over) root.addChild(st.pieces.over.sprite);
    }
    st.on = true;
    frame.visible = false;
    const css = stage.cssScale > 0 ? stage.cssScale : 1;
    const u = 1 / css;
    const t = Math.max(1, u);
    st.u = u;
    st.t = t;
    const V = stage.safeRect as Rectangle;
    const m = 4 * u;
    const X0 = V.x + m;
    const X1 = V.x + V.width - m;
    // The frame over the shown safe area's height too (a tablet's or an unfolded phone's is taller than the window:
    // no band of the battle above and below the sheet).
    const y0 = V.y + m;
    const y1 = V.y + V.height - m;
    const ky = (y1 - y0) / (F.y1 - F.y0);
    const Y = (y: number) => y0 + (y - F.y0) * ky;
    const col = (x0: number, x1: number): Area => ({ x: x0, y: F.y0, width: x1 - x0, height: F.y1 - F.y0 });
    const span = (x0: number, x1: number): Area => ({ x: x0, y: y0, width: x1 - x0, height: y1 - y0 });
    const leftW = F.left - F.x0;
    const rightW = F.x1 - F.right;
    // The touch controls' close button over the frame's right part: the panels end before it.
    const cr = closeRect(fwp);
    let inner1 = X1 - rightW;
    if (cr && cr.x < inner1 + rightW && cr.x + cr.width > inner1 - 8 * u) inner1 = Math.min(inner1, cr.x - 6 * u);
    // The note: 12 CSS px, one line.
    let noteH = 0;
    if (note) {
      const o = st.orig.get(note)!;
      note.scaleX = o.sx;
      note.scaleY = o.sy;
      note.width = o.width!;
      const room = X1 - rightW - (X0 + leftW);
      const k = Math.min(t, room / Math.max(1, note.textWidth + 8));
      note.scaleX = note.scaleY = k;
      note.width = room / k;
      note.x = X0 + leftW;
      note.y = y0 + 1;
      noteH = (note.textHeight + 4) * k;
    }
    const top = Math.max(Y(F.top) + 4 * u, y0 + 1 + noteH + 3 * u);
    const bottom = Y(F.bottom) - 4 * u;
    const footer = 26 * u;
    const p = Math.max(PITCH, CELL_CSS * u);
    const gx0 = X0 + leftW + 8 * u;
    // Beside the cells where the description fits there in DESC_W at 12 CSS px, else over them.
    const descW = DESC_W * t;
    const side = (bottom - top) / t >= DESC_H && (inner1 - descW - 8 * u - gx0) / p >= 6;
    st.mode = side ? 'side' : 'over';
    const light1 = side ? inner1 - descW : inner1;
    const gx1 = light1 - 8 * u;
    const cols = Math.max(1, Math.floor((gx1 - gx0) / p));
    const rows = Math.max(1, Math.floor((bottom - footer - 4 * u - top) / p));
    // As many cells as fit at CELL_CSS, then as large as the room lets them be; the grid and the footer row under it
    // in the middle of the room.
    const pp = Math.min((gx1 - gx0) / cols, (bottom - footer - 4 * u - top) / rows, p * 1.25);
    const gridX = gx0 + (gx1 - gx0 - cols * pp) / 2;
    const gridY = top + (bottom - top - (rows * pp + 4 * u + footer)) / 2;
    st.grid = { x: gridX, y: gridY, p: pp, cols, rows };
    const P = st.pieces;
    P.l?.place(col(F.x0, F.left), span(X0, X0 + leftW));
    P.g?.place(col(F.light, F.light + 1), span(X0 + leftW - 0.5, light1 + 0.5));
    P.d?.place(col(F.dark0, F.dark1), side ? span(light1, inner1) : null);
    P.b?.place(col(F.body, F.body + 1), inner1 < X1 - rightW - 0.5 ? span(inner1 - 0.5, X1 - rightW + 0.5) : null);
    P.r?.place(col(F.right, F.x1), span(X1 - rightW, X1));
    // The cells: scaled, placed each frame (frame(): the game renders them in its 8 columns).
    const c = pp / PITCH;
    cont.scaleX = cont.scaleY = c;
    cont.x = gridX + 2 * c;
    cont.y = gridY + 3 * c;
    st.cells = '';
    // The description: in the dark panel, or (over the cells) where frame() shows it.
    st.panel = side ? { x: light1, y: Y(F.top), width: inner1 - light1, height: Y(F.bottom) - Y(F.top) } : { x: gx0 - 8 * u, y: y0 + 3 * u, width: Math.min(inner1 - gx0 + 8 * u, DESC_OVER_W * t), height: Y(F.bottom) - y0 - 3 * u };
    placeDesc(st, root, st.panel);
    // Over the cells the description lets the finger through: it shows when a finger presses a cell (the game's
    // hover), and the release over it must still be that cell's click (the pick).
    letFingerThrough(st, [name, desc], !side);
    if (!side) {
      st.hover = '\u0000';
      showOver(st, root, fwp);
    } else {
      P.over?.place(col(F.dark0, F.dark1), null);
      name.visible = desc.visible = true;
    }
    st.info = { x: gridX, y: gridY + rows * pp + 4 * u, width: cols * pp, height: footer };
    placeCloseUnder(close, cr, st.orig, root);
    placeCells(st, root);
    // (not at the first layout: the game renders the sheet's cells right after putting it on the stage)
    if (wasGrid && wasGrid !== `${cols}x${rows}`) regrid(fwp, root);
  },

  frame(root, ctx) {
    const st = sheetState(root);
    // The touch controls' close button placed anew (they lay themselves out after the stage's resize).
    const cr = closeRect(ctx.fwp);
    const ck = cr ? `${Math.round(cr.x)},${Math.round(cr.y)},${Math.round(cr.width)}` : '';
    if (ck !== st.closeKey) {
      st.closeKey = ck;
      WEAPON_SHEET.layout(root, ctx);
    }
    if (!st.on) return;
    placeCells(st, root);
    if (st.mode === 'over') showOver(st, root, ctx.fwp);
    else if (st.panel && String(root.weapon_desc.text) !== st.descKey) placeDesc(st, root, st.panel);
  },
};

/**
 * The description's fields (name, text) at 12 CSS px in the panel `a`; a text too long for it there (the longest ones
 * over the cells of a small phone) a little smaller, down to MIN_TEXT of that.
 */
function placeDesc(st: SheetState, root: any, a: Area): void {
  const u = st.u;
  const t = st.t;
  const name = root.weapon_name;
  const desc = root.weapon_desc;
  const on = st.orig.get(name)!;
  const od = st.orig.get(desc)!;
  name.scaleX = on.sx * t;
  name.scaleY = on.sy * t;
  name.x = a.x + 6 * u;
  name.y = a.y + 6 * u;
  name.width = (a.width - 12 * u) / (on.sx * t);
  const y = name.y + (on.height! + 4) * on.sy * t;
  const room = a.y + a.height - 4 * u - y;
  let k = t;
  for (let n = 0; n < 8; n++) {
    desc.scaleX = od.sx * k;
    desc.scaleY = od.sy * k;
    desc.x = a.x + 3 * u;
    desc.y = y;
    desc.width = (a.width - 6 * u) / (od.sx * k);
    desc.height = Math.max(20, room / (od.sy * k));
    if ((desc.textHeight + 4) * od.sy * k <= room || k <= t * MIN_TEXT) break;
    k = Math.max(t * MIN_TEXT, k * 0.95);
  }
  st.descKey = String(desc.text);
}

/**
 * The description's fields mouse-transparent (`through`: over the cells, a 16:9 phone) or as the original has them.
 * (The panel's piece under them is already: pieceOf.)
 */
function letFingerThrough(st: SheetState, fields: any[], through: boolean): void {
  for (const o of fields) {
    if (!o) continue;
    if (through) {
      if (!st.mouse.has(o)) st.mouse.set(o, !!o.mouseEnabled);
      o.mouseEnabled = false;
    } else if (st.mouse.has(o)) {
      o.mouseEnabled = st.mouse.get(o)!;
      st.mouse.delete(o);
    }
  }
}

/** How much smaller than TEXT_CSS the longest description may get to fit (a small phone). */
const MIN_TEXT = 0.88;

/** A 16:9 phone: the description over the cells while a finger holds a weapon (the game's hover), away from it. */
function showOver(st: SheetState, root: any, fwp: any): void {
  const i = fwp && fwp._interf;
  const held = i && i.cur_weapon ? String(i.cur_weapon) : '';
  if (held === st.hover) return;
  st.hover = held;
  const name = root.weapon_name;
  const desc = root.weapon_desc;
  const over = st.pieces?.over;
  const a = st.panel;
  if (!held || !a || !st.grid) {
    over?.place(a ?? ({ x: 0, y: 0, width: 1, height: 1 } as Area), null);
    name.visible = desc.visible = false;
    st.overShown = false;
    return;
  }
  // Away from the held weapon: at the right when it is in the left half of the cells, else at the left.
  const cont = root.in_game_weapon_container;
  let cx = st.grid.x;
  for (let n = 0; n < cont.numChildren; n++) {
    const cell = cont.getChildAt(n);
    if (String(cell.name).startsWith(`ingame_weapon_${held}%`)) {
      const b = cell.getBounds(root);
      cx = b.x + b.width / 2;
      break;
    }
  }
  const g = st.grid;
  const room = { x0: g.x - 8 * st.u, x1: g.x + g.cols * g.p + 8 * st.u };
  const w = Math.min(a.width, room.x1 - room.x0);
  const x = cx < (room.x0 + room.x1) / 2 ? room.x1 - w : room.x0;
  const r = { x, y: a.y, width: w, height: a.height };
  over?.place({ x: F.dark0, y: F.y0 + (F.top - F.y0), width: F.dark1 - F.dark0, height: F.bottom - F.top }, r);
  // Over the cells: the panel, then the text over it.
  if (over) root.setChildIndex(over.sprite, root.numChildren - 1);
  root.setChildIndex(name, root.numChildren - 1);
  root.setChildIndex(desc, root.numChildren - 1);
  placeDesc(st, root, r);
  name.visible = desc.visible = true;
  st.overShown = true;
}

/** The cells in the grid's columns (the game renders them in 8) and the page arrows in its last two cells. */
function placeCells(st: SheetState, root: any): void {
  const g = st.grid;
  const cont = root.in_game_weapon_container;
  if (!g || !cont) return;
  const n = cont.numChildren;
  const first = n ? cont.getChildAt(0) : null;
  const last = n ? cont.getChildAt(n - 1) : null;
  const key = `${n}|${first ? `${first.name},${first.x},${first.y}` : ''}|${last ? `${last.name},${last.x},${last.y}` : ''}|${g.cols}x${g.rows}`;
  if (key !== st.cells) {
    for (let i = 0; i < n; i++) {
      const cell = cont.getChildAt(i);
      cell.x = (i % g.cols) * PITCH;
      cell.y = Math.floor(i / g.cols) * PITCH;
    }
    const l2 = n ? cont.getChildAt(n - 1) : null;
    st.cells = `${n}|${first ? `${first.name},${first.x},${first.y}` : ''}|${l2 ? `${l2.name},${l2.x},${l2.y}` : ''}|${g.cols}x${g.rows}`;
  }
  // The arrows in the grid's last two cells (the game shows them only when there are more pages).
  const u = st.u;
  // (a cell's art reaches 4 % of the pitch into the slot right of it and 6 % into the one under it: the arrows keep
  // clear of that)
  const size = Math.min(Math.max(ARROW_CSS * u, g.p * 0.86), g.p * 0.94 - 1);
  const slot = (c: number): Area => ({ x: g.x + c * g.p + g.p * 0.04, y: g.y + (g.rows - 1) * g.p + g.p * 0.06, width: g.p * 0.96, height: g.p * 0.94 });
  const put = (btn: any, a: Area) => {
    if (!btn) return;
    const o = st.orig.get(btn);
    if (!o) return;
    btn.scaleX = o.sx;
    btn.scaleY = o.sy;
    const b0 = btn.getBounds(root);
    const k = size / Math.max(1, b0.width, b0.height);
    btn.scaleX = o.sx * k;
    btn.scaleY = o.sy * k;
    const b = btn.getBounds(root);
    btn.x += a.x + a.width / 2 - (b.x + b.width / 2);
    btn.y += a.y + a.height / 2 - (b.y + b.height / 2);
  };
  put(root.in_game_prev_page, slot(Math.max(0, g.cols - 2)));
  put(root.in_game_next_page, slot(g.cols - 1));
}

/** The grid's size changed while the sheet is open: its pages again (the game's render, the page kept in range). */
function regrid(fwp: any, root: any): void {
  const i = fwp && fwp._interf;
  if (!i || !root.stage || !i.cur_screen) return;
  const top = i.cur_screen[i.cur_screen[0]];
  if (!top || top.link !== root || typeof i.render !== 'function') return;
  i.shop_page = 1;
  i.render();
}
